import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { gbp, imgUrl } from "@/lib/api";
import { T } from "@/constants/testIds";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal, shipping } = useCart();
  const navigate = useNavigate();
  const freeGap = Math.max(0, 50 - subtotal);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-stone-200 bg-[#FFFFFF]"
          >
            <div className="flex items-center justify-between border-b border-stone-200 px-6 py-5">
              <h2 className="font-display text-2xl font-black uppercase tracking-wide text-stone-950">
                Your Bag <span className="text-[#E52E39]">({items.length})</span>
              </h2>
              <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-950" aria-label="Close bag">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <p className="font-display text-xl uppercase tracking-wide text-stone-700">Your bag is empty</p>
                  <p className="max-w-[240px] text-sm text-stone-400">
                    Pieces handled personally, delivered to your door.
                  </p>
                  <button
                    onClick={() => {
                      setOpen(false);
                      navigate("/shop");
                    }}
                    className="mt-2 bg-[#E52E39] px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c9202a]"
                  >
                    Browse the shop
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((it) => (
                    <li key={`${it.product_id}-${it.size}-${it.colour || ""}`} className="flex gap-4 border border-stone-200 bg-[#FAF8F4] p-3">
                      <div className="h-20 w-16 shrink-0 overflow-hidden bg-[#F1EEE6]">
                        {it.image && <img src={imgUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-display text-sm font-bold uppercase leading-tight tracking-wide text-stone-950">
                              {it.name}
                            </p>
                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-stone-500">
                              Size {it.size}{it.colour ? ` • ${it.colour}` : ""} • {it.delivery_time}
                            </p>
                          </div>
                          <button
                            onClick={() => remove(it.product_id, it.size, it.colour)}
                            className="text-stone-400 hover:text-[#E52E39]"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-stone-300">
                            <button
                              onClick={() => updateQty(it.product_id, it.size, it.colour, it.qty - 1)}
                              className="px-2 py-1 text-stone-700 hover:text-stone-950"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="min-w-[24px] text-center font-mono text-xs text-stone-950">{it.qty}</span>
                            <button
                              onClick={() => updateQty(it.product_id, it.size, it.colour, it.qty + 1)}
                              className="px-2 py-1 text-stone-700 hover:text-stone-950"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="font-mono text-sm font-semibold text-[#C79500]">{gbp(it.price * it.qty)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-stone-200 px-6 py-5">
                {freeGap > 0 ? (
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-stone-500">
                    Add <span className="text-[#C79500]">{gbp(freeGap)}</span> more for free UK delivery
                  </p>
                ) : (
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-[#C79500]">
                    Free UK delivery unlocked
                  </p>
                )}
                <div className="mb-1 flex justify-between font-mono text-xs uppercase tracking-widest text-stone-500">
                  <span>Subtotal</span>
                  <span>{gbp(subtotal)}</span>
                </div>
                <div className="mb-3 flex justify-between font-mono text-xs uppercase tracking-widest text-stone-500">
                  <span>Delivery</span>
                  <span>{shipping === 0 ? "FREE" : gbp(shipping)}</span>
                </div>
                <button
                  data-testid={T.cartCheckout}
                  onClick={() => {
                    setOpen(false);
                    navigate("/checkout");
                  }}
                  className="w-full bg-[#E52E39] py-4 font-display text-lg font-black uppercase tracking-wide text-white transition-all hover:bg-[#c9202a] hover:shadow-[0_0_30px_rgba(229,46,57,0.35)]"
                >
                  Checkout • Card
                </button>
                <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-stone-400">
                  Secure payments via Stripe
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
